import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography } from "@mui/material";
import { KeyboardArrowDown, Search as SearchIcon } from "@mui/icons-material";
import GoldField from "./GoldField";

// ════════════════════════════════════════════════════════════════════════
//  CompanyStoreSelector — ONE combined "Company → Store" filter dropdown,
//  replacing the old pattern of a separate Company dropdown + separate
//  Store dropdown duplicated on every page (see MarketSyncDashboardPage's
//  old local CompanySelector/StoreSelector, which this is modeled on —
//  same visual language, so it drops into an existing header without
//  looking out of place).
//
//  Selection model — ONE source of truth: `selectedStoreIds`
//    null      → every store selected (the default, "All")
//    []        → nothing selected
//    [ids...]  → exactly these store_ids selected
//  A company's own checkbox is DERIVED from its stores' state (checked /
//  indeterminate / unchecked — a standard checkbox-tree) rather than being
//  a second, separately-tracked selection that could drift out of sync
//  with the stores underneath it. Clicking a company's checkbox selects or
//  deselects every one of its stores in one go, per the brief: "i select
//  one company so his all store select if i unselect so his all store
//  unselect".
//
//  onChange fires with BOTH derived values together — { companyIds,
//  storeIds } — so a page that still needs a separate "which companies are
//  in scope" value (e.g. to send as a `company_ids` query param alongside
//  `store_ids`) gets it for free, instead of computing it itself. companyIds
//  follows the exact same null/[]/[ids] convention, derived as "every
//  company that owns at least one of the currently-selected stores" (or
//  null/[] to mirror storeIds' own all/none state).
//
//  `stores` must be the FULL, unfiltered list across every company (not
//  pre-scoped to a company selection) — this component does its own
//  filtering/search over the complete set, so a deselected company's
//  stores are still searchable/re-selectable.
//
//  `orderCounts` (optional): { [store_id]: { total, today } } — shown as a
//  small caption under each store's name.
// ════════════════════════════════════════════════════════════════════════

// Cream/gold admin theme (same as the Account / Settings pages) — every
// page that uses this selector (MarketSync DropShipping + its sub-pages,
// Settings) is on that theme.
const GOLD_ACCENT = "#b8892c";
const UI = { card: "#fffcf7", border: "#ece2cc", borderSoft: "#f3ecdc", text: "#1d1a14", textSub: "#4a4234", muted: "#6b6459", mutedLight: "#7d7466", rowHover: "rgba(184,137,44,0.08)" };

const storeKey   = (s) => String(s.store_id ?? s._id);
const companyKey = (c) => String(c._id);
const companyLabel = (c) => c.name || c.company_name || "Company";
const storeLabel   = (s) => s.store_name || `Store ${s.store_id}`;

const fmtCount = (n) => {
    const v = Number(n || 0);
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return String(v);
};

export default function CompanyStoreSelector({
    companies = [],
    stores = [],
    selectedStoreIds = null,
    onChange,
    orderCounts = {},
    // inline: render the list itself (title, search box on top, company →
    // store checkboxes) as an always-open card instead of a dropdown — used
    // inside forms (Settings → Users) where a popup would be clipped.
    inline = false,
    // fitAnchor: used in the SIDEBAR — the trigger fills its container and the
    // list opens right under it with EXACTLY the container's width (it follows
    // the sidebar when it is resized), drawn in a portal so the sidebar's own
    // scroll/overflow can't clip it.
    fitAnchor = false,
    title = "Filter by Company & Store",
}) {
    const [open, setOpen] = useState(false);
    // Dropdown opens centered under the button; if that would cross the
    // visible edge it slides just enough to stay fully visible. "Visible
    // edge" = the screen (16px margin) AND any parent that clips overflow
    // (e.g. a page wrapper with overflow:hidden next to the sidebar).
    const rootRef = useRef(null);
    const [popLeft, setPopLeft] = useState(0);
    const [popWidth, setPopWidth] = useState(320);
    useLayoutEffect(() => {
        if (!open) return undefined;
        const place = () => {
            const el = rootRef.current;
            if (!el) return;
            const root = el.getBoundingClientRect();
            const btn = (el.firstElementChild || el).getBoundingClientRect(); // the pill itself
            const vw = window.innerWidth;
            let minL = 16;
            let maxR = vw - 16;
            for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
                const ov = window.getComputedStyle(p).overflowX;
                if (ov && ov !== "visible") {
                    const r = p.getBoundingClientRect();
                    minL = Math.max(minL, r.left + 8);
                    maxR = Math.min(maxR, r.right - 8);
                }
            }
            const popW = Math.max(220, Math.min(320, maxR - minL));
            const centered = btn.left + (btn.width - popW) / 2;
            const clamped = Math.max(minL, Math.min(centered, maxR - popW));
            setPopWidth(Math.round(popW));
            setPopLeft(Math.round(clamped - root.left));
        };
        place();
        window.addEventListener("resize", place);
        return () => window.removeEventListener("resize", place);
    }, [open]);
    // fitAnchor geometry: the trigger's on-screen box, kept current while open.
    const [anchorBox, setAnchorBox] = useState(null); // { left, top, width }
    useLayoutEffect(() => {
        if (!fitAnchor || !open) return undefined;
        const measure = () => {
            const el = rootRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            setAnchorBox({ left: Math.round(r.left), top: Math.round(r.bottom + 8), width: Math.round(r.width) });
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        const ro = typeof ResizeObserver !== "undefined" && rootRef.current ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(rootRef.current);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
            if (ro) ro.disconnect();
        };
    }, [fitAnchor, open]);
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState({}); // companyId -> bool, only used while NOT searching
    const [searchCollapsed, setSearchCollapsed] = useState({}); // companyId -> true when closed by hand WHILE searching (reset on every new search)
    useEffect(() => { setSearchCollapsed({}); }, [search]);
    const PURPLE = GOLD_ACCENT;
    const U = UI;

    const isStoreChecked = (key) => selectedStoreIds === null || selectedStoreIds.includes(String(key));

    const totalStores    = stores.length;
    const selectedCount  = selectedStoreIds === null ? totalStores : selectedStoreIds.length;
    const isAllSelected  = selectedStoreIds === null || (totalStores > 0 && selectedCount === totalStores);
    const isNoneSelected = selectedStoreIds !== null && selectedCount === 0;

    // How many distinct companies actually have a selected store right now
    // — drives the trigger's own "N Stores · M Companies" label.
    const selectedCompanyCount = useMemo(() => {
        if (selectedStoreIds === null) return companies.length;
        if (selectedStoreIds.length === 0) return 0;
        const sel = new Set(selectedStoreIds);
        const ids = new Set();
        stores.forEach((s) => { if (sel.has(storeKey(s))) ids.add(String(s.sellvia_dashboard_id)); });
        return ids.size;
    }, [selectedStoreIds, stores, companies.length]);

    const label = isNoneSelected
        ? "No Stores Selected"
        : isAllSelected
            ? "All Companies · All Stores"
            : `${selectedCompanyCount} Compan${selectedCompanyCount === 1 ? "y" : "ies"} · ${selectedCount} Store${selectedCount === 1 ? "" : "s"}`;
    const dotColor = isNoneSelected ? "#DC2626" : "#ffffff";
    // Trigger pill — same brand purple as the page's "Sync Live"/"Sync All"
    // button, white text/icon, whenever something is selected (matches across
    // every selection state, incl. "All Companies · All Stores"); the
    // "nothing selected" state keeps its own red warning look.
    const TRIGGER_FILL = "#8c6a1c";
    const TRIGGER_FILL_HOVER = "#6f5212";

    // Emits both derived values together — see the doc comment above for why.
    const emit = (newStoreIds) => {
        let companyIds;
        if (newStoreIds === null) {
            companyIds = null;
        } else if (newStoreIds.length === 0) {
            companyIds = [];
        } else {
            const sel = new Set(newStoreIds);
            const ids = new Set();
            stores.forEach((s) => { if (sel.has(storeKey(s))) ids.add(String(s.sellvia_dashboard_id)); });
            companyIds = Array.from(ids);
        }
        onChange?.({ companyIds, storeIds: newStoreIds });
    };

    const toggleStore = (key) => {
        const strKey = String(key);
        if (selectedStoreIds === null) {
            emit(stores.filter((s) => storeKey(s) !== strKey).map(storeKey));
        } else if (selectedStoreIds.includes(strKey)) {
            emit(selectedStoreIds.filter((s) => s !== strKey));
        } else {
            emit([...selectedStoreIds, strKey]);
        }
    };

    const toggleCompany = (company, compStores) => {
        const compKeys = compStores.map(storeKey);
        const currentlyAllChecked = compKeys.length > 0 && compKeys.every(isStoreChecked);
        const base = selectedStoreIds === null ? stores.map(storeKey) : selectedStoreIds;
        if (currentlyAllChecked) {
            emit(base.filter((k) => !compKeys.includes(k)));
        } else {
            const set = new Set(base);
            compKeys.forEach((k) => set.add(k));
            emit(Array.from(set));
        }
    };

    // While searching, Select All / Deselect All act on the stores the search shows (companies and stores
    // that match), not on everything.
    const toggleAll = () => {
        if (!searching) { emit(isAllSelected ? [] : null); return; }
        const keys = rows.flatMap((r) => r.visibleStores.map(storeKey));
        if (keys.length === 0) return;
        const base = selectedStoreIds === null ? stores.map(storeKey) : selectedStoreIds;
        if (keys.every(isStoreChecked)) {
            const drop = new Set(keys);
            emit(base.filter((k) => !drop.has(k)));
        } else {
            emit(Array.from(new Set([...base, ...keys])));
        }
    };

    const q = search.trim().toLowerCase();
    const searching = q.length > 0;
    const textMatches = (t) => !searching || (t || "").toString().toLowerCase().includes(q);

    // A company shows while searching if its OWN name matches (in which case
    // every one of its stores shows too) or at least one of its stores'
    // name/id matches (in which case only THOSE stores show).
    const rows = useMemo(() => companies.map((company) => {
        const compStores = stores.filter((s) => String(s.sellvia_dashboard_id) === companyKey(company));
        if (!searching) return { company, compStores, visibleStores: compStores, show: true };
        const companyMatch = textMatches(companyLabel(company));
        const visibleStores = companyMatch ? compStores : compStores.filter((s) => textMatches(storeLabel(s)) || textMatches(String(s.store_id)));
        return { company, compStores, visibleStores, show: companyMatch || visibleStores.length > 0 };
    }).filter((r) => r.show), [companies, stores, q]); // eslint-disable-line react-hooks/exhaustive-deps
    const shownKeys = rows.flatMap((r) => r.visibleStores.map(storeKey));
    const allShownSelected = searching ? (shownKeys.length > 0 && shownKeys.every(isStoreChecked)) : isAllSelected;

    const CheckSquare = ({ checked, indeterminate, onClick, size = 16 }) => (
        <Box
            onClick={onClick}
            sx={{
                width: size, height: size, borderRadius: 0.5, flexShrink: 0, cursor: "pointer",
                border: checked || indeterminate ? "none" : "2px solid #d9c79e",
                bgcolor: checked || indeterminate ? PURPLE : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all .15s",
            }}
        >
            {checked && <Box component="span" sx={{ color: "#fff", fontSize: size - 6, lineHeight: 1 }}>✓</Box>}
            {!checked && indeterminate && <Box sx={{ width: size - 8, height: 2, bgcolor: "#fff", borderRadius: 1 }} />}
        </Box>
    );

    // Title bar + search box + company → store list (shared by both modes).
    const panelBody = (
        <>
                        <Box sx={{ px: 1.5, py: 1, gap: 0.8, background: "linear-gradient(180deg,#8c6a1c,#6f5212)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap" }}>
                            <Typography noWrap sx={{ color: "#fff", fontWeight: 700, fontSize: 11, minWidth: 0, flex: "1 1 auto" }}>{title}</Typography>
                            <Box onClick={toggleAll} sx={{ flexShrink: 0, cursor: "pointer", px: 0.8, py: 0.2, borderRadius: 99, bgcolor: "rgba(255,255,255,.2)", "&:hover": { bgcolor: "rgba(255,255,255,.35)" } }}>
                                <Typography noWrap sx={{ color: "#fff", fontSize: 9, fontWeight: 700, whiteSpace: "nowrap" }}>{allShownSelected ? "Deselect All" : "Select All"}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ px: 1.5, pt: 1.2, pb: 0.8, borderBottom: `1px solid ${U.borderSoft}` }}>
                            <GoldField
                                size="small" autoFocus={!inline} icon={SearchIcon}
                                placeholder="Search company or store…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ "& .MuiOutlinedInput-root": { minHeight: 36, fontSize: 12 } }}
                            />
                        </Box>

                        <Box sx={{ maxHeight: 320, minHeight: 0, overflowY: "auto", py: 0.5, "&::-webkit-scrollbar": { width: "5px" }, "&::-webkit-scrollbar-thumb": { background: U.border, borderRadius: "4px" } }}>
                            {rows.length === 0 && (
                                <Typography sx={{ fontSize: 11, color: U.mutedLight, px: 2, py: 2, textAlign: "center" }}>
                                    {searching ? "No company or store matches your search." : "No companies found."}
                                </Typography>
                            )}
                            {rows.map(({ company, compStores, visibleStores }) => {
                                const compKeys = compStores.map(storeKey);
                                const checkedKeys = compKeys.filter(isStoreChecked);
                                const companyChecked       = compKeys.length > 0 && checkedKeys.length === compKeys.length;
                                const companyIndeterminate = checkedKeys.length > 0 && !companyChecked;
                                const isExpanded = searching ? !searchCollapsed[companyKey(company)] : !!expanded[companyKey(company)];
                                return (
                                    <React.Fragment key={companyKey(company)}>
                                        <Box
                                            sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 2, py: 1, cursor: "pointer", bgcolor: isExpanded ? U.rowHover : "transparent", transition: "background .15s", "&:hover": { bgcolor: U.borderSoft } }}
                                            onClick={() => (searching
                                                ? setSearchCollapsed((prev) => ({ ...prev, [companyKey(company)]: !prev[companyKey(company)] }))
                                                : setExpanded((prev) => ({ ...prev, [companyKey(company)]: !prev[companyKey(company)] })))}
                                        >
                                            <CheckSquare checked={companyChecked} indeterminate={companyIndeterminate} onClick={(e) => { e.stopPropagation(); toggleCompany(company, compStores); }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: U.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{companyLabel(company)}</Typography>
                                                <Typography sx={{ fontSize: 10, color: U.mutedLight }}>{checkedKeys.length}/{compStores.length} store{compStores.length === 1 ? "" : "s"} selected</Typography>
                                            </Box>
                                            <KeyboardArrowDown sx={{ fontSize: 14, color: U.mutedLight, transform: isExpanded ? "rotate(180deg)" : "none", transition: ".2s" }} />
                                        </Box>
                                        {isExpanded && visibleStores.map((store) => {
                                            const key = storeKey(store);
                                            const checked = isStoreChecked(key);
                                            const counts = orderCounts[key] || orderCounts[String(store.store_id)];
                                            return (
                                                <Box
                                                    key={key}
                                                    onClick={() => toggleStore(key)}
                                                    sx={{ display: "flex", alignItems: "center", gap: 1.2, pl: 4.2, pr: 2, py: 0.9, cursor: "pointer", bgcolor: checked ? U.rowHover : "transparent", transition: "background .15s", "&:hover": { bgcolor: U.borderSoft } }}
                                                >
                                                    <CheckSquare checked={checked} size={15} onClick={(e) => { e.stopPropagation(); toggleStore(key); }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: U.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{storeLabel(store)}</Typography>
                                                        {counts && (
                                                            <Typography sx={{ fontSize: 9.5, color: U.mutedLight }}>
                                                                {fmtCount(counts.total)} order{counts.total === 1 ? "" : "s"} · {fmtCount(counts.today)} new today
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
        </>
    );

    if (inline) {
        return (
            <Box sx={{ bgcolor: U.card, borderRadius: 3, border: `1px solid ${U.border}`, overflow: "hidden", boxShadow: "0 4px 16px rgba(122,87,18,.08)" }}>
                {panelBody}
            </Box>
        );
    }

    return (
        // minWidth/maxWidth let the pill shrink (label gets "…") when it
        // shares a narrow row with other filters on a phone.
        <Box ref={rootRef} sx={{ position: "relative", minWidth: 0, maxWidth: "100%", ...(fitAnchor ? { width: "100%" } : {}) }}>
            <Box
                onClick={() => setOpen((o) => !o)}
                sx={{
                    display: fitAnchor ? "flex" : "inline-flex", alignItems: "center", gap: 0.8, maxWidth: "100%",
                    ...(fitAnchor ? { width: "100%", boxSizing: "border-box" } : {}),
                    px: 2, py: 0.9, borderRadius: 99, cursor: "pointer",
                    border: `1.5px solid ${isNoneSelected ? "#FCA5A5" : TRIGGER_FILL}`,
                    bgcolor: isNoneSelected ? U.card : TRIGGER_FILL,
                    boxShadow: isNoneSelected ? "0 2px 8px rgba(122,87,18,.1)" : "0 4px 12px rgba(111,82,18,.30)",
                    transition: "all .2s",
                    "&:hover": { borderColor: isNoneSelected ? "#F87171" : TRIGGER_FILL_HOVER, bgcolor: isNoneSelected ? U.borderSoft : TRIGGER_FILL_HOVER },
                }}
            >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: isNoneSelected ? "#DC2626" : "#ffffff", whiteSpace: "nowrap", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", ...(fitAnchor ? { flex: 1 } : {}) }}>{label}</Typography>
                <KeyboardArrowDown sx={{ fontSize: 14, color: isNoneSelected ? U.mutedLight : "#ffffff", transform: open ? "rotate(180deg)" : "none", transition: ".2s" }} />
            </Box>

            {open && fitAnchor && anchorBox && createPortal(
                <>
                    <Box onClick={() => { setOpen(false); setSearch(""); }} sx={{ position: "fixed", inset: 0, zIndex: 1349 }} />
                    <Box sx={{
                        position: "fixed", top: anchorBox.top, left: anchorBox.left, width: anchorBox.width, zIndex: 1350,
                        maxHeight: `calc(100vh - ${anchorBox.top + 12}px)`, display: "flex", flexDirection: "column",
                        bgcolor: U.card, borderRadius: 3, boxSizing: "border-box",
                        boxShadow: "0 12px 40px rgba(122,87,18,.22)", border: `1px solid ${U.border}`, overflow: "hidden",
                    }}>
                        {panelBody}
                    </Box>
                </>,
                document.body
            )}

            {open && !fitAnchor && (
                <>
                    <Box onClick={() => { setOpen(false); setSearch(""); }} sx={{ position: "fixed", inset: 0, zIndex: 99 }} />
                    <Box sx={{
                        position: "absolute", top: "calc(100% + 8px)", left: popLeft, zIndex: 100,
                        width: popWidth, maxWidth: "calc(100vw - 32px)", bgcolor: U.card, borderRadius: 3,
                        boxShadow: "0 12px 40px rgba(122,87,18,.18)", border: `1px solid ${U.border}`,
                        overflow: "hidden",
                    }}>
                        {panelBody}
                    </Box>
                </>
            )}
        </Box>
    );
}
